import api from '../../utils/api';

export type CoverageFilter = 'all' | 'unique' | 'overlap' | 'exclusive';

export interface AmbulanceServiceAreaCompareParams {
  pinCode?: string;
  areaName?: string;
  search?: string;
  isActive?: boolean | '';
  coverage?: CoverageFilter;
  includeInactiveVendors?: boolean;
}

export interface CompareServiceAreaRow {
  serviceAreaId: string;
  pinCode: string | null;
  areaName: string | null;
  latitude: number | null;
  longitude: number | null;
  radiusKm: number | null;
  isActive: boolean;
  updatedAt?: string;
}

export interface CompareVendorRow {
  vendorId: string;
  generatedId: string | null;
  agencyName: string | null;
  ownerName: string | null;
  contactNumber: string | null;
  email: string | null;
  phoneNumber: string | null;
  status: string;
  isActive: boolean;
  city: string | null;
  state: string | null;
  officePinCode: string | null;
  numOfAmbulances: number | null;
  is24x7Available: boolean | null;
  serviceAreas: CompareServiceAreaRow[];
  exclusiveAreas?: Array<{
    areaKey: string;
    pinCode: string | null;
    displayName: string;
    areaNames: string[];
  }>;
}

export interface CompareAreaGroup {
  areaKey: string;
  pinCode: string | null;
  areaNames: string[];
  displayName: string;
  vendorCount: number;
  coverageType: 'unique' | 'overlap';
  vendors: CompareVendorRow[];
}

export interface AmbulanceServiceAreaCompareSummary {
  totalServiceAreas: number;
  totalAreaGroups: number;
  matchedAreaGroups: number;
  totalVendors: number;
  uniqueCoverageAreas: number;
  overlapAreas: number;
  uniquePinCodes: number;
}

export interface AmbulanceServiceAreaCompareData {
  filters: {
    pinCode: string | null;
    areaName: string | null;
    search: string | null;
    isActive: boolean | null;
    coverage: CoverageFilter;
    includeInactiveVendors: boolean;
  };
  summary: AmbulanceServiceAreaCompareSummary;
  areas: CompareAreaGroup[];
  exclusiveVendors?: CompareVendorRow[];
}

export interface AmbulanceServiceAreaCompareResponse {
  success: boolean;
  message?: string;
  data?: AmbulanceServiceAreaCompareData;
}

export async function compareAmbulanceServiceAreas(
  params: AmbulanceServiceAreaCompareParams = {}
): Promise<AmbulanceServiceAreaCompareResponse> {
  const query: Record<string, string | boolean> = {};
  if (params.pinCode?.trim()) query.pinCode = params.pinCode.trim();
  if (params.areaName?.trim()) query.areaName = params.areaName.trim();
  if (params.search?.trim()) query.search = params.search.trim();
  if (params.isActive === true || params.isActive === false) {
    query.isActive = params.isActive;
  }
  if (params.coverage && params.coverage !== 'all') {
    query.coverage = params.coverage === 'exclusive' ? 'unique' : params.coverage;
  }
  if (params.includeInactiveVendors) {
    query.includeInactiveVendors = true;
  }

  const response = await api.get<AmbulanceServiceAreaCompareResponse>(
    '/master-admin/ambulance-service-areas/compare',
    { params: query }
  );
  return response.data;
}

export interface PlaceSuggestion {
  placeId: string;
  description: string;
}

export interface PlaceDetails {
  placeId: string;
  formattedAddress: string;
  latitude: number;
  longitude: number;
  postalCode?: string | null;
}

export async function searchAmbulancePlaces(input: string): Promise<PlaceSuggestion[]> {
  const response = await api.get<{
    success: boolean;
    data?: { suggestions?: PlaceSuggestion[] };
  }>('/master-admin/ambulance-service-areas/places', { params: { input } });
  return response.data.data?.suggestions || [];
}

export async function getAmbulancePlaceDetails(placeId: string): Promise<PlaceDetails | null> {
  const response = await api.get<{ success: boolean; data?: PlaceDetails }>(
    '/master-admin/ambulance-service-areas/place-details',
    { params: { placeId } }
  );
  return response.data.data || null;
}

export const AMBULANCE_TYPES = [
  { value: 'EECO_AMBULANCE_BLS', label: 'Eeco Ambulance (BLS)' },
  { value: 'TEMPO_TRAVELLER_BLS', label: 'Tempo Traveller (BLS)' },
  { value: 'CARDIAC_AMBULANCE_ALS', label: 'Cardiac Ambulance (ALS)' },
] as const;

export const AMBULANCE_FACILITIES = [
  { value: 'WITH_OXYGEN', label: 'With oxygen' },
  { value: 'WITHOUT_OXYGEN', label: 'Without oxygen' },
  { value: 'AC_WITH_OXYGEN', label: 'AC with oxygen' },
  { value: 'AC_WITHOUT_OXYGEN', label: 'AC without oxygen' },
  { value: 'ATTENDANT', label: 'Attendant' },
] as const;

export interface SuggestAmbulanceRequest {
  ambulanceType: string;
  facilities?: string[];
  isDeadBody?: boolean;
  exclusiveOnly?: boolean;
  pickupPinCode?: string;
  pickupLat?: number | null;
  pickupLng?: number | null;
  pickupAddress?: string;
  dropLat?: number | null;
  dropLng?: number | null;
  dropAddress?: string;
  totalDistanceKm?: number | null;
  limit?: number;
}

export interface SuggestAmbulancePricing {
  baseFare: number;
  subTotal: number;
  totalPayableAmount: number;
  pricingSource: string;
  currency: string;
}

export interface SuggestAmbulanceRow {
  vendorId: string;
  generatedId: string | null;
  agencyName: string | null;
  ownerName: string | null;
  contactNumber: string | null;
  email: string | null;
  phoneNumber: string | null;
  status: string | null;
  isActive: boolean;
  city: string | null;
  state: string | null;
  officePinCode: string | null;
  numOfAmbulances: number | null;
  is24x7Available: boolean | null;
  exclusiveCoverage: boolean | null;
  pricing: SuggestAmbulancePricing | null;
}

export interface SuggestAmbulanceData {
  trip: {
    ambulanceType: string;
    facilities: string[];
    isDeadBody: boolean;
    exclusiveOnly: boolean;
    pickupPinCode: string | null;
    pickupLat: number | null;
    pickupLng: number | null;
    pickupAddress: string | null;
    dropLat: number | null;
    dropLng: number | null;
    dropAddress: string | null;
    distanceKm: number;
    distanceSource: string | null;
    durationText: string | null;
  };
  summary: {
    eligibleCount: number;
    withPricing: number;
    lowestFare: number | null;
  };
  suggestions: SuggestAmbulanceRow[];
}

export async function suggestAmbulancesWithPricing(
  body: SuggestAmbulanceRequest
): Promise<{ success: boolean; message?: string; data?: SuggestAmbulanceData }> {
  const response = await api.post('/master-admin/ambulance-service-areas/suggest', body);
  return response.data;
}
